import { DragDropModule } from '@angular/cdk/drag-drop';
import { OverlayModule } from '@angular/cdk/overlay';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, Injectable, NgModule, isDevMode } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatRadioModule } from '@angular/material/radio';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatToolbarModule } from '@angular/material/toolbar';
import { BrowserModule, HAMMER_GESTURE_CONFIG, HammerGestureConfig, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, Router } from '@angular/router';
import * as Sentry from '@sentry/angular-ivy';
import * as Hammer from 'hammerjs';
import { QrCodeModule } from 'ng-qrcode';
import { ClickOutsideModule } from 'src/app/shared/directive/click-outside/click-outside.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BasicLayoutComponent } from './layouts/basic-layout/basic-layout.component';
import { BottomMenuComponent } from './layouts/bottom-menu/bottom-menu.component';
import { CurrencyWalletComponent } from './layouts/currency-wallet/currency-wallet.component';
import { FooterModule } from './layouts/footer/footer.module';
import { H5TopMenuModule } from './layouts/h5-top-menu/h5-top-menu.module';
import { HeaderComponent } from './layouts/header/header.component';
import { LeftMenuComponent } from './layouts/left-menu/left-menu.component';
import { StatisticsPanelComponent } from './layouts/statistics-panel/statistics-panel.component';
import { TopBarComponent } from './layouts/top-bar/top-bar.component';
import { PageNotFoundModule } from './pages/page-not-found/page-not-found.module';
import { WalletTransferComponent } from './pages/user-asset/wallet-transfer/wallet-transfer.component';
import { AssetsValueComponent } from './shared/components/assets-value/assets-value.component';
import { AuthPromptComponent } from './shared/components/auth-prompt/auth-prompt.component';
import { BindPopupComponent } from './shared/components/bind-popup/bind-popup.component';
import { BindVerifyPopupComponent } from './shared/components/bind-verify-popup/bind-verify-popup.component';
import { CodeInputModule } from './shared/components/code-input/code-input.module';
import { CustomizeFormModule } from './shared/components/customize-form/customize-form.module';
import { EddPopupComponent } from './shared/components/edd-popup/edd-popup.component';
import { EmptyModule } from './shared/components/empty/empty.module';
import { FaceVerifyComponent } from './shared/components/face-verify/face-verify.component';
import { LogoModule } from './shared/components/logo/logo.module';
import { PhoneNumberSelecterComponent } from './shared/components/phone-number-selecter/phone-number-selecter.component';
import { PreviewMediaComponent } from './shared/components/preview-media/preview-media.component';
import { ScrollbarModule } from './shared/components/scrollbar/scrollbar.module';
import { SelectCurrencyModule } from './shared/components/select-currency/select-currency.module';
import { ThemeSwitchComponent } from './shared/components/theme-switch/theme-switch.component';
import { ToastComponent } from './shared/components/toast/toast.component';
import { IntersectionObserverModule } from './shared/directive/intersection-observer/intersection-observer.module';
import { LoadingModule } from './shared/directive/loading/loading.module';
import { MouseScrollModule } from './shared/directive/mouse-scroll/mouse-scroll.module';
import './shared/extensions/number-extensions';

import { APP_BASE_HREF } from '@angular/common';
import { ServiceWorkerModule } from '@angular/service-worker';
import { FingerprintJSPro, FingerprintjsProAngularModule } from '@fingerprintjs/fingerprintjs-pro-angular';
import { environment } from 'src/environments/environment';
import { CustomReuseStrategy } from './custom-reuse-strategy';
import { HeaderLevel2MenuComponent } from './layouts/header-level2-menu/header-level2-menu.component';
import { LeftMenuProfileInfoComponent } from './layouts/left-menu/left-menu-profile-info/left-menu-profile-info.component';
// 只匯入 @defer 外殼，不匯入 SendbirdModule —— 後者會把整棵 layouts/sendbird-chat 綁進 initial bundle。
import { SendbirdChatPanelComponent } from './layouts/sendbird-chat/sendbird-chat-panel.component';
import { UserLayoutComponent } from './layouts/user-layout/user-layout.component';
// 只匯入 @defer 外殼，不匯入 ChatModule —— 後者會把整棵 pages/chat 綁進 initial bundle。
import { ChatPanelComponent } from './pages/chat/chat-panel.component';
import { SearchGameModule } from './pages/minigame/search-game/search-game.module';
import { NotificationOverviewDialog } from './pages/notification-center/dialog/overview-dialog/overview.dialog';
import { CSButtonComponent } from './shared/components/cs-button/cs-button.component';
import { CSIconButtonComponent } from './shared/components/cs-icon-button/cs-icon-button.component';
import { CSInputComponent } from './shared/components/cs-input/cs-input.component';
import { CsStandardTabComponent } from './shared/components/cs-standard-tab/cs-standard-tab.component';
import { CSSwitchComponent } from './shared/components/cs-switch/cs-switch.component';
import { CSTabComponent } from './shared/components/cs-tab/cs-tab.component';
import { CustomerServiceModule } from './shared/components/customer-service/customer-service.module';
import { FiatDisplaySwitchComponent } from './shared/components/fiat-display-switch/fiat-display-switch.component';
import { StandardPopupModule } from './shared/components/standard-popup/standard-popup.module';
import { UserAvatarComponent } from './shared/components/user-avatar/user-avatar.component';
import { LifeObserveModule } from './shared/directive/life-observe/life-observe.module';
import { AvatarDefaultPipe } from './shared/pipes/avatar-default.pipe';
import { CountryFilterPipe } from './shared/pipes/cuontry-filter.pipe';
import { CurrencyValuePipe } from './shared/pipes/currency-value.pipe';
import { FilterByKeyValuePipe } from './shared/pipes/filter-by-key-value.pipe';
import { PipesModule } from './shared/pipes/pipes.module';
import { RegionRestrictionService } from './shared/service/region-restriction.service';
@Injectable()
class HammerConfig extends HammerGestureConfig {
  overrides = {
    pan: {},
    swipe: {
      direction: Hammer.DIRECTION_ALL,
    },
    press: { time: 1000 },
  };
}

@NgModule({
  declarations: [
    AppComponent,
    PhoneNumberSelecterComponent,
    BasicLayoutComponent,
    UserLayoutComponent,
    HeaderComponent,
    AuthPromptComponent,
    TopBarComponent,
    CurrencyWalletComponent,
    LeftMenuComponent,
    BottomMenuComponent,
    ThemeSwitchComponent,
    ToastComponent,
    WalletTransferComponent,
    PreviewMediaComponent,
    StatisticsPanelComponent,
    BindPopupComponent,
    BindVerifyPopupComponent,
    FaceVerifyComponent,
    EddPopupComponent,
    HeaderLevel2MenuComponent,
  ],
  imports: [
    H5TopMenuModule,
    CustomerServiceModule,
    LifeObserveModule,
    PageNotFoundModule,
    StandardPopupModule,
    CustomizeFormModule,
    LogoModule,
    OverlayModule,
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    HammerModule,
    FormsModule,
    ChatPanelComponent,
    QrCodeModule,
    MatDialogModule,
    MatCheckboxModule,
    CodeInputModule,
    MatSidenavModule,
    MatSlideToggleModule,
    PipesModule,
    MatToolbarModule,
    IntersectionObserverModule,
    ClickOutsideModule,
    ScrollbarModule,
    LoadingModule,
    EmptyModule,
    DragDropModule,
    MatBadgeModule,
    FooterModule,
    MouseScrollModule,
    SelectCurrencyModule,
    MatRadioModule,
    SearchGameModule,
    FingerprintjsProAngularModule.forRoot({
      loadOptions: {
        apiKey: environment.common.fpApiKey,
        endpoint: [environment.common.fpEndpoint],
        scriptUrlPattern: [
          `${environment.common.fpEndpointJS}/v<version>/<apiKey>/loader_v<loaderVersion>.js`,
          FingerprintJSPro.defaultScriptUrlPattern,
        ],
      },
    }),
    ServiceWorkerModule.register(`${window.location.origin}/ngsw-worker.js?v=${environment.version}`, {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    LeftMenuProfileInfoComponent,
    AssetsValueComponent,
    CSButtonComponent,
    CSIconButtonComponent,
    UserAvatarComponent,
    CsStandardTabComponent,
    CSInputComponent,
    CSTabComponent,
    CSSwitchComponent,
    FiatDisplaySwitchComponent,
    NotificationOverviewDialog,
    SendbirdChatPanelComponent,
  ],
  providers: [
    { provide: APP_BASE_HREF, useValue: window.location.origin },
    // 這兩個 provider 原本來自 imports 裡的 DatepickerModule。該模組匯出的
    // <app-datepicker> 全專案僅用於 wallet-history 的 customized-time-dialog，
    // app.module 的匯入純粹把 Material datepicker 拖進 initial bundle，因此移除，
    // 並把兩個 provider 原樣搬到此處（設定值逐字相同）以維持 root 設定不變。
    //
    // ⚠️ 但經盤點，這份 root 設定**目前很可能沒有任何消費者** ——
    // 兩個實際用到日期的元件都自備更近的 provider：
    //   <app-datepicker>        → DatepickerModule 內的同名 provider（YYYY-MM-DD）
    //   <cs-date-range-picker>  → 元件層級 DATE_FORMATS（DD/MM/YYYY）
    // 兩者亦各自匯入 MatMomentDateModule，故 root 少了 DateAdapter / MAT_DATE_LOCALE
    // 也不影響 —— 但要留意這代表本次改動**並非嚴格等價**，只是目前無人受影響。
    //
    // 保留是為了在盤點有遺漏時仍能運作；若後續確認無誤可連同移除。
    // 移除前務必重新確認上述兩條路徑，不要只看 grep `.module.ts`（provider 掛在元件上）。
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'YYYY-MM-DD',
        },
        display: {
          dateInput: 'YYYY-MM-DD',
          monthYearLabel: 'YYYY-M',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'YYYY MMM',
        },
      },
    },
    {
      provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS,
      useValue: { strict: true, useUtc: false },
    },
    CountryFilterPipe,
    CurrencyValuePipe,
    FilterByKeyValuePipe,
    AvatarDefaultPipe,
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: HammerConfig,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (regionRestrictionService: RegionRestrictionService) => () => regionRestrictionService.initialize(),
      deps: [RegionRestrictionService],
      multi: true,
    },
    {
      provide: RouteReuseStrategy,
      useClass: CustomReuseStrategy,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
